import * as React from 'react';
import { Clock, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface TimePickerProps {
  value: string;
  onChange: (time: string) => void;
  minTime?: string;
  placeholder?: string;
}

const ITEM_HEIGHT = 36;

export const TimePicker: React.FC<TimePickerProps> = ({
  value,
  onChange,
  minTime,
  placeholder = 'Selecione a hora'
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const hoursRef = React.useRef<HTMLDivElement>(null);
  const minutesRef = React.useRef<HTMLDivElement>(null);

  const [selectedHour, selectedMinute] = value ? value.split(':') : ['', ''];

  const hours = React.useMemo(() =>
    Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')),
  []);

  const minutes = React.useMemo(() =>
    Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0')),
  []);

  const handleHourChange = (hour: string) => {
    const minute = selectedMinute || '00';
    onChange(`${hour}:${minute}`);
  };

  const handleMinuteChange = (minute: string) => {
    const hour = selectedHour || '00';
    onChange(`${hour}:${minute}`);
  };

  const scrollToSelected = React.useCallback(() => {
    if (selectedHour && hoursRef.current) {
      const idx = hours.indexOf(selectedHour);
      hoursRef.current.scrollTop = idx * ITEM_HEIGHT - ITEM_HEIGHT * 4;
    }
    if (selectedMinute && minutesRef.current) {
      const idx = minutes.indexOf(selectedMinute);
      minutesRef.current.scrollTop = idx * ITEM_HEIGHT - ITEM_HEIGHT * 4;
    }
  }, [selectedHour, selectedMinute, hours, minutes]);

  React.useEffect(() => {
    if (isOpen) {
      // Pequeno delay para garantir renderização
      const timer = setTimeout(scrollToSelected, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen, scrollToSelected]);

  const isDisabled = (hour: string, minute: string) => {
    if (!minTime) return false;
    const [minHour, minMinute] = minTime.split(':');
    const h = parseInt(hour, 10);
    const m = parseInt(minute, 10);
    const mh = parseInt(minHour, 10);
    const mm = parseInt(minMinute, 10);
    return h < mh || (h === mh && m < mm);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'w-full justify-between text-left font-normal h-11 px-3 rounded-xl border border-gray-200 hover:border-green-500 hover:bg-white transition-colors',
            !value && 'text-muted-foreground'
          )}
        >
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-500" />
            {value ? (
              <span className="text-gray-900">{value.replace(':', ' : ')}</span>
            ) : (
              <span>{placeholder}</span>
            )}
          </div>
          <ChevronUp className={cn('h-4 w-4 text-gray-400 transition-transform', isOpen && 'rotate-180')} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[280px] p-0 rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
        align="start"
      >
        <div className="space-y-3 p-4">
          {/* Cabeçalho das colunas */}
          <div className="flex items-center justify-center pb-2 border-b border-gray-100">
            <div className="w-[90px] text-center">
              <span className="text-sm font-medium text-green-600 pb-0.5 inline-block">
                Horas
              </span>
            </div>
            <div className="w-8" />
            <div className="w-[90px] text-center">
              <span className="text-sm font-medium text-gray-400 pb-0.5 inline-block">
                Minutos
              </span>
            </div>
          </div>

          {/* Colunas scrolláveis */}
          <div className="flex items-start justify-center">
            {/* Coluna de Horas */}
            <div className="w-[90px] flex flex-col items-center">
              <button
                type="button"
                className="p-1 text-gray-400 hover:text-gray-600"
                onClick={() => {
                  if (hoursRef.current) {
                    hoursRef.current.scrollBy({ top: -ITEM_HEIGHT, behavior: 'smooth' });
                  }
                }}
              >
                <ChevronUp className="h-4 w-4" />
              </button>
              <div
                ref={hoursRef}
                className="h-[220px] overflow-y-auto w-full scrollbar-hide"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                <div className="py-2 space-y-1">
                  {hours.map((hour) => {
                    const isSelected = selectedHour === hour;
                    const disabled = !selectedMinute
                      ? false
                      : isDisabled(hour, selectedMinute);
                    return (
                      <button
                        key={hour}
                        type="button"
                        data-hour={hour}
                        onClick={() => !disabled && handleHourChange(hour)}
                        disabled={disabled}
                        className={cn(
                          'w-full py-1.5 text-sm rounded-lg transition-colors text-center block',
                          isSelected
                            ? 'bg-green-600 text-white font-medium'
                            : disabled
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-gray-700 hover:bg-gray-100'
                        )}
                      >
                        {hour}
                      </button>
                    );
                  })}
                </div>
              </div>
              <button
                type="button"
                className="p-1 text-gray-400 hover:text-gray-600 rotate-180"
                onClick={() => {
                  if (hoursRef.current) {
                    hoursRef.current.scrollBy({ top: ITEM_HEIGHT, behavior: 'smooth' });
                  }
                }}
              >
                <ChevronUp className="h-4 w-4" />
              </button>
            </div>

            {/* Separador */}
            <div className="w-8 flex items-center justify-center" style={{ height: '260px' }}>
              <span className="text-lg font-medium text-gray-400">:</span>
            </div>

            {/* Coluna de Minutos */}
            <div className="w-[90px] flex flex-col items-center">
              <button
                type="button"
                className="p-1 text-gray-400 hover:text-gray-600"
                onClick={() => {
                  if (minutesRef.current) {
                    minutesRef.current.scrollBy({ top: -ITEM_HEIGHT, behavior: 'smooth' });
                  }
                }}
              >
                <ChevronUp className="h-4 w-4" />
              </button>
              <div
                ref={minutesRef}
                className="h-[220px] overflow-y-auto w-full scrollbar-hide"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                <div className="py-2 space-y-1">
                  {minutes.map((minute) => {
                    const isSelected = selectedMinute === minute;
                    const disabled = !selectedHour
                      ? false
                      : isDisabled(selectedHour, minute);
                    return (
                      <button
                        key={minute}
                        type="button"
                        data-minute={minute}
                        onClick={() => !disabled && handleMinuteChange(minute)}
                        disabled={disabled}
                        className={cn(
                          'w-full py-1.5 text-sm rounded-lg transition-colors text-center block',
                          isSelected
                            ? 'bg-green-600 text-white font-medium'
                            : disabled
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-gray-700 hover:bg-gray-100'
                        )}
                      >
                        {minute}
                      </button>
                    );
                  })}
                </div>
              </div>
              <button
                type="button"
                className="p-1 text-gray-400 hover:text-gray-600 rotate-180"
                onClick={() => {
                  if (minutesRef.current) {
                    minutesRef.current.scrollBy({ top: ITEM_HEIGHT, behavior: 'smooth' });
                  }
                }}
              >
                <ChevronUp className="h-4 w-4" />
              </button>
            </div>
          </div>


        </div>
      </PopoverContent>
    </Popover>
  );
};
